/**
 * THE FORUM — the one room every shop is in.
 *
 * Anthony: "put the forums in everyones more options in shop desk for
 * every shop. This is the one area that other shops can see forums and
 * interact etc."
 *
 * Every other screen in this app shows one shop its own work. This one
 * shows everybody everybody's, on purpose, because a shop in Hemet
 * reading the answer somebody in Phoenix already got is the entire point
 * of having it. Migration 0119 writes that intent into the policies
 * rather than leaving it to this file.
 *
 * WHAT A POST CARRIES: the question, the answers, the writer's first name
 * and their shop's name. Nothing else, and nothing pulled from a shop's
 * own data. The names come from `forum_authors`, a definer view holding
 * exactly those two fields, so a forum cannot become a way to read the
 * profiles table across shops.
 *
 * THE NAMES ARE FETCHED SEPARATELY, not joined. PostgREST will not embed
 * a view that has no foreign key, and inventing one to satisfy a join
 * would put a real constraint on the database for the sake of a screen.
 * Two reads and a map is smaller than that.
 */

import { db } from "../data/client.ts";
import { esc, ago, mount } from "../ui/dom.ts";

type Topic = {
  id: string; title: string; body: string; category: string;
  status: string; pinned: boolean; reply_count: number;
  last_reply_at: string | null; created_at: string;
  author_id: string; account_id: string | null;
};

type Reply = {
  id: string; topic_id: string; body: string; created_at: string; author_id: string;
};

type Author = { author_id: string; first_name: string | null; shop_name: string | null };

/**
 * The shelves. "Everything else" is deliberately last and deliberately
 * vague: a forum that makes somebody choose a category correctly before
 * they can ask for help has put a quiz in front of the help.
 */
const CATEGORIES = [
  { key: "all", label: "All" },
  { key: "getting-started", label: "Getting started" },
  { key: "jobs", label: "Jobs and quotes" },
  { key: "money", label: "Money" },
  { key: "photos", label: "Photos" },
  { key: "general", label: "Everything else" },
];

let filter = "all";

/** Who wrote it, in the only two words this screen is allowed to know. */
async function authorsFor(ids: string[]): Promise<Map<string, Author>> {
  const map = new Map<string, Author>();
  const want = [...new Set(ids)].filter(Boolean);
  if (!want.length) return map;
  const sb = await db();
  const { data } = await sb.from("forum_authors")
    .select("author_id,first_name,shop_name").in("author_id", want);
  for (const a of (data ?? []) as Author[]) map.set(a.author_id, a);
  return map;
}

const byline = (a: Author | undefined): string => {
  const who = a?.first_name?.trim() || "Somebody";
  const shop = a?.shop_name?.trim();
  return shop ? `${who} at ${shop}` : who;
};

/** A body is plain text. It is escaped and then its line breaks kept. */
const bodyHtml = (s: string): string => esc(s).replace(/\n/g, "<br>");

/* ── the list ──────────────────────────────────────────────────────── */

export async function forumPage(): Promise<void> {
  mount(`<div class="page">
      <header class="page-head">
        <a class="back" href="#/more">&larr; More</a>
        <h1>Forum</h1>
        <p>Every shop on Shop Desk is in here. Ask anything, answer anybody.</p>
      </header>
      <div class="tabs wide tabs-wrap" id="fo-tabs">${CATEGORIES.map((c) =>
        `<button class="tab ${c.key === filter ? "on" : ""}" data-cat="${c.key}">${esc(c.label)}</button>`)
        .join("")}</div>
      <div class="card">
        <div class="pad"><button class="btn primary wide" id="fo-new">Ask the other shops</button></div>
      </div>
      <div id="fo-list"><div class="loading">Reading&hellip;</div></div>
    </div>`);

  document.querySelectorAll<HTMLButtonElement>("#fo-tabs .tab").forEach((b) =>
    b.addEventListener("click", () => { filter = b.dataset["cat"] ?? "all"; void forumPage(); }));
  document.getElementById("fo-new")?.addEventListener("click", () => { location.hash = "#/forum/new"; });

  const host = document.getElementById("fo-list");
  if (!host) return;

  const sb = await db();
  let q = sb.from("forum_topics")
    .select("id,title,body,category,status,pinned,reply_count,last_reply_at,created_at,author_id,account_id")
    .order("pinned", { ascending: false })
    .order("created_at", { ascending: false })
    .limit(100);
  if (filter !== "all") q = q.eq("category", filter);

  const { data, error } = await q;
  if (error) {
    host.innerHTML = `<div class="card"><div class="empty bad">${esc(error.message)}</div></div>`;
    return;
  }
  const topics = (data ?? []) as Topic[];
  if (!topics.length) {
    host.innerHTML = `<div class="card"><div class="empty">Nothing here yet under that heading.
      Be the first to ask.</div></div>`;
    return;
  }

  const who = await authorsFor(topics.map((t) => t.author_id));
  host.innerHTML = `<div class="card">${topics.map((t) => `
    <a class="row" href="#/forum/${esc(t.id)}">
      <div class="row-main">
        <div class="row-title"><span class="num">${esc(t.title)}</span>
          ${t.pinned ? `<span class="pill">pinned</span>` : ""}
          ${t.status === "answered" ? `<span class="pill q-approved">answered</span>` : ""}
          ${t.status === "closed" ? `<span class="pill">closed</span>` : ""}</div>
        <div class="row-sub">${esc(byline(who.get(t.author_id)))}
          &middot; ${esc(ago(t.last_reply_at ?? t.created_at))}
          &middot; ${t.reply_count} ${t.reply_count === 1 ? "reply" : "replies"}</div>
      </div>
      <div class="row-side"><div class="when">&rarr;</div></div>
    </a>`).join("")}</div>`;
}

/* ── one topic, and answering it ───────────────────────────────────── */

export async function forumTopicPage(id: string): Promise<void> {
  mount(`<div class="page">
      <header class="page-head"><a class="back" href="#/forum">&larr; Forum</a>
        <h1 id="fo-title">Reading&hellip;</h1></header>
      <div id="fo-thread"></div>
    </div>`);

  const host = document.getElementById("fo-thread");
  if (!host) return;

  const sb = await db();
  const [{ data: tRow, error: tErr }, { data: rRows }] = await Promise.all([
    sb.from("forum_topics").select("*").eq("id", id).maybeSingle(),
    sb.from("forum_replies").select("id,topic_id,body,created_at,author_id")
      .eq("topic_id", id).order("created_at", { ascending: true }),
  ]);

  if (tErr || !tRow) {
    host.innerHTML = `<div class="card"><div class="empty bad">${esc(tErr?.message ?? "That topic is gone.")}</div></div>`;
    return;
  }
  const topic = tRow as Topic;
  const replies = (rRows ?? []) as Reply[];
  const who = await authorsFor([topic.author_id, ...replies.map((r) => r.author_id)]);

  const title = document.getElementById("fo-title");
  if (title) title.textContent = topic.title;

  host.innerHTML = `
    <div class="card">
      <div class="card-head">${esc(byline(who.get(topic.author_id)))}
        &middot; ${esc(ago(topic.created_at))}</div>
      <div class="pad"><p>${bodyHtml(topic.body)}</p></div>
    </div>
    ${replies.map((r) => `
      <div class="card">
        <div class="card-head">${esc(byline(who.get(r.author_id)))}
          &middot; ${esc(ago(r.created_at))}</div>
        <div class="pad"><p>${bodyHtml(r.body)}</p></div>
      </div>`).join("")}
    ${topic.status === "closed" ? `
      <div class="card"><div class="empty">This one is closed.</div></div>` : `
      <div class="card">
        <div class="card-head">Answer it</div>
        <div class="pad">
          <textarea id="fo-reply" rows="5" placeholder="What do you know about this?"></textarea>
          <button class="btn primary wide" id="fo-send">Post the answer</button>
          <p class="msg bad" id="fo-err" hidden></p>
        </div>
      </div>`}`;

  document.getElementById("fo-send")?.addEventListener("click", async () => {
    const box = document.getElementById("fo-reply") as HTMLTextAreaElement | null;
    const err = document.getElementById("fo-err");
    const body = (box?.value ?? "").trim();
    if (body.length < 2) return;
    const btn = document.getElementById("fo-send") as HTMLButtonElement | null;
    if (btn) { btn.disabled = true; btn.textContent = "Posting"; }
    /* account_id is read from the signed in profile rather than passed in,
       because the policy checks it against the same row and a guess would
       simply be refused. */
    const { data: me } = await sb.auth.getUser();
    const uid = me.user?.id;
    const { data: mine } = await sb.from("profiles").select("account_id").eq("id", uid ?? "").maybeSingle();
    const { error } = await sb.from("forum_replies").insert({
      topic_id: id, author_id: uid, account_id: (mine as { account_id: string } | null)?.account_id, body,
    });
    if (error) {
      if (err) { err.hidden = false; err.textContent = error.message; }
      if (btn) { btn.disabled = false; btn.textContent = "Post the answer"; }
      return;
    }
    void forumTopicPage(id);
  });
}

/* ── asking ────────────────────────────────────────────────────────── */

export function forumNewPage(): void {
  mount(`<div class="page">
      <header class="page-head"><a class="back" href="#/forum">&larr; Forum</a>
        <h1>Ask the other shops</h1>
        <p>Every shop on Shop Desk can read this and answer it.</p></header>
      <div class="card">
        <div class="pad">
          <label for="fo-t">What is the question</label>
          <input id="fo-t" type="text" maxlength="200" placeholder="Short, the way you would say it out loud">
          <label for="fo-c">Where does it belong</label>
          <select id="fo-c">${CATEGORIES.filter((c) => c.key !== "all")
            .map((c) => `<option value="${c.key}">${esc(c.label)}</option>`).join("")}</select>
          <label for="fo-b">Tell them what is happening</label>
          <textarea id="fo-b" rows="8" placeholder="What you did, what you expected, what happened instead."></textarea>
          <p class="fine">Do not paste a customer's details in here. Every shop can read it.</p>
          <button class="btn primary wide" id="fo-post">Post it</button>
          <p class="msg bad" id="fo-err2" hidden></p>
        </div>
      </div>
    </div>`);

  document.getElementById("fo-post")?.addEventListener("click", async () => {
    const title = (document.getElementById("fo-t") as HTMLInputElement | null)?.value.trim() ?? "";
    const body = (document.getElementById("fo-b") as HTMLTextAreaElement | null)?.value.trim() ?? "";
    const category = (document.getElementById("fo-c") as HTMLSelectElement | null)?.value ?? "general";
    const err = document.getElementById("fo-err2");
    const show = (m: string) => { if (err) { err.hidden = false; err.textContent = m; } };
    if (title.length < 3) return show("Give it a title somebody could search for.");
    if (body.length < 3) return show("Say what is happening, so somebody can answer.");

    const btn = document.getElementById("fo-post") as HTMLButtonElement | null;
    if (btn) { btn.disabled = true; btn.textContent = "Posting"; }
    const sb = await db();
    const { data: me } = await sb.auth.getUser();
    const uid = me.user?.id;
    const { data: mine } = await sb.from("profiles").select("account_id").eq("id", uid ?? "").maybeSingle();
    const { data, error } = await sb.from("forum_topics").insert({
      author_id: uid, account_id: (mine as { account_id: string } | null)?.account_id,
      title, body, category,
    }).select("id").maybeSingle();
    if (error || !data) {
      show(error?.message ?? "That did not post.");
      if (btn) { btn.disabled = false; btn.textContent = "Post it"; }
      return;
    }
    location.hash = `#/forum/${(data as { id: string }).id}`;
  });
}
